import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BarChart3, Code2, Loader2, Megaphone, ShieldCheck, Target } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, get, errMsg } from "@/lib/api";
import { FieldLabel, PageHeader, Panel } from "@/admin/ui";
import { useRefresh } from "@/admin/hooks";

const CONSENT = { always: "Siempre, aunque el lector rechace las cookies", analytics: "Solo si acepta las cookies analíticas", marketing: "Solo si acepta las cookies de publicidad" };

const TOOLS = [
  { k: "ga4_id", title: "Google Analytics 4", icon: BarChart3, ph: "G-XXXXXXXXXX", consent: "analytics", help: "En Google Analytics: Administrar → Flujos de datos → tu web → «ID de medición»." },
  { k: "ads_id", title: "Google Ads", icon: Megaphone, ph: "AW-123456789", consent: "marketing", help: "En Google Ads: Objetivos → Conversiones → Configuración → «Etiqueta de Google» (empieza por AW-)." },
  { k: "meta_pixel_id", title: "Píxel de Meta", icon: Target, ph: "123456789012345", consent: "marketing", help: "En Meta Business: Administrador de eventos → Orígenes de datos → tu píxel → «Identificador»." },
];

const SLOTS = [
  { slot: "head", title: "Código en el <head>", help: "Etiquetas meta, estilos o scripts que el servicio pida poner en la cabecera." },
  { slot: "body_start", title: "Código al inicio del <body>", help: "Lo que deba ir justo después de abrir el body, como la parte <noscript> de Google Tag Manager." },
  { slot: "body_end", title: "Código al final del <body>", help: "Widgets de chat, encuestas, mapas de calor u otros scripts de terceros." },
];

const Badge = ({ on, testid }) => (
  <span className={`shrink-0 whitespace-nowrap font-mono text-[10.5px] uppercase tracking-wider border px-2 py-0.5 ${on ? "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900" : "text-muted-foreground border-border"}`} data-testid={testid}>
    {on ? "Activo" : "Sin configurar"}
  </span>
);

const ToolCard = ({ tool, f, set }) => (
  <Panel title={tool.title} actions={<Badge on={!!f[tool.k]} testid={`tracking-${tool.k}-status`} />} testid={`tracking-${tool.k}-panel`}>
    <div className="flex items-center gap-3">
      <span className="h-10 w-10 shrink-0 grid place-items-center bg-muted"><tool.icon className="h-5 w-5 text-primary" /></span>
      <Input value={f[tool.k]} onChange={(e) => set({ [tool.k]: e.target.value })} placeholder={tool.ph} className="font-mono" data-testid={`tracking-${tool.k}-input`} />
    </div>
    <p className="text-xs text-muted-foreground mt-3 leading-relaxed">{tool.help}</p>
    <p className="text-xs mt-2 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />{CONSENT[tool.consent]}.</p>
  </Panel>
);

const CodeSlot = ({ s, f, set }) => {
  const consent = f[`${s.slot}_consent`];
  return (
    <Panel title={s.title} description={s.help} testid={`tracking-${s.slot}-panel`} actions={<Badge on={!!f[`${s.slot}_code`].trim()} testid={`tracking-${s.slot}-status`} />}>
      <FieldLabel>Cuándo se carga</FieldLabel>
      <Select value={consent} onValueChange={(v) => set({ [`${s.slot}_consent`]: v })}>
        <SelectTrigger data-testid={`tracking-${s.slot}-consent-select`}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(CONSENT).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent>
      </Select>
      {consent === "always" && <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">Úsalo solo para código que no instale cookies ni siga al lector (por ejemplo, etiquetas de verificación).</p>}
      <Textarea value={f[`${s.slot}_code`]} onChange={(e) => set({ [`${s.slot}_code`]: e.target.value })} rows={8} spellCheck={false}
        placeholder={'<script>\n  // pega aquí el código completo\n</script>'} className="mt-4 font-mono text-xs leading-relaxed bg-background" data-testid={`tracking-${s.slot}-code-input`} />
    </Panel>
  );
};

export default function TrackingCodes() {
  const { data } = useQuery({ queryKey: ["admin-tracking"], queryFn: () => get("/admin/tracking") });
  const [f, setF] = useState(null);
  const refresh = useRefresh();
  useEffect(() => { if (data) setF(data); }, [data]);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const save = useMutation({
    mutationFn: () => api.put("/admin/tracking", f).then((r) => r.data),
    onSuccess: (d) => { setF(d); refresh(); toast.success("Códigos guardados: ya están activos en la web"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!f) return <div className="py-20 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const saveBtn = (id) => <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid={`tracking-save-btn${id}`}>{save.isPending && <Loader2 className="animate-spin" />}Guardar códigos</Button>;
  return (
    <div data-testid="admin-tracking-page">
      <PageHeader kicker="Integraciones" title="Códigos y seguimiento" description="Conecta Google Analytics, Google Ads y Meta, o pega cualquier otro código. Solo se carga en la web pública, nunca en este panel.">{saveBtn("")}</PageHeader>
      <div className="grid lg:grid-cols-3 gap-6">{TOOLS.map((t) => <ToolCard key={t.k} tool={t} f={f} set={set} />)}</div>
      <p className="kicker text-[10px] text-muted-foreground mt-10 mb-3 flex items-center gap-2"><Code2 className="h-3.5 w-3.5" />Código libre</p>
      <div className="grid xl:grid-cols-3 gap-6">{SLOTS.map((s) => <CodeSlot key={s.slot} s={s} f={f} set={set} />)}</div>
      <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-l-4 border-emerald-500 bg-card px-5 py-4" data-testid="tracking-privacy-note">
        <p className="text-sm max-w-3xl">La <strong>política de cookies</strong> y la de <strong>privacidad</strong> se actualizan solas con lo que actives aquí, y el aviso de cookies muestra la categoría «Publicidad» cuando hace falta.</p>
        <Button variant="outline" className="bg-card" asChild><Link to="/admin/paginas" data-testid="tracking-pages-link">Ver páginas legales</Link></Button>
      </div>
      <div className="mt-8 flex justify-end">{saveBtn("-bottom")}</div>
    </div>
  );
}
